// src/pages/ApplicationChampionshipSeasonCreate/index.tsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@chakra-ui/react';

import { PageContent } from '@/components/PageContent';
import { SeasonType, StageType } from '@/model';

import { breadcrumb } from './breadcrumb';
import {
  seasonFormSchema,
  SeasonFormData,
} from '../ApplicationChampionshipSeason/shared/schemas';
import { DEFAULT_GROUPS_COUNT, DEFAULT_STANDINGS_COLUMNS } from '../ApplicationChampionshipSeason/shared/constants';
import { useStageSeeding } from '../ApplicationChampionshipSeason/shared/hooks/useStageSeeding';
import { buildStagesBatchItems } from '../ApplicationChampionshipSeason/shared/utils/buildStagesBatchItems';
import {
  usePostStagesBatchMutation,
  usePostStageBatchTeamsMutation,
} from '@/services/queries/stage';
import { usePostSeasonTeamsBatchMutation } from '@/services/queries/season-team';
import {
  SeasonWizardTabs,
  WizardStep,
} from '../ApplicationChampionshipSeason/shared/components/SeasonWizardTabs';
import { Step1SeasonInfo } from '../ApplicationChampionshipSeason/shared/steps/Step1Info/Step1SeasonInfo';
import { Step2SeasonStages } from '../ApplicationChampionshipSeason/shared/steps/Step2Stages/Step2SeasonStages';
import { Step3SeasonTeams } from '../ApplicationChampionshipSeason/shared/steps/Step3Teams/Step3SeasonTeams';
import { Step4SeasonReview } from '../ApplicationChampionshipSeason/shared/steps/Step4Review/Step4SeasonReview';

const SEASONS_LIST_ROUTE = '/application/championships/seasons';

export const ApplicationChampionshipSeasonCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const [tabIndex, setTabIndex] = useState(0);
  const [furthestStep, setFurthestStep] = useState(0);

  const form = useForm<SeasonFormData>({
    defaultValues: {
      step: 1,
      championship_uuid: '',
      season_year: '',
      season_type: undefined,
      is_filtered: false,
      is_active: true,
      groups_count: DEFAULT_GROUPS_COUNT,
      group_stages: [],
      knockout_stages: [],
      league_stage_config: {
        direct_qualifiers_count: 0,
        playoff_qualifiers_count: 0,
        secondary_qualifiers_count: 0,
        relegated_count: 0,
        direct_qualifiers_label: '',
        playoff_qualifiers_label: '',
        secondary_qualifiers_label: '',
        relegated_qualifiers_label: '',
      },
      standings_columns: DEFAULT_STANDINGS_COLUMNS,
    },
    resolver: zodResolver(seasonFormSchema),
    mode: 'onSubmit',
  });

  useStageSeeding(form);

  const postStagesBatch = usePostStagesBatchMutation();
  const postSeasonTeamsBatch = usePostSeasonTeamsBatchMutation();
  const postStageBatchTeams = usePostStageBatchTeamsMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const goToStep = (index: number) => {
    setTabIndex(index);
    setFurthestStep(previous => Math.max(previous, index));
  };

  const handleCancel = () => navigate(SEASONS_LIST_ROUTE);

  const handleConfirm = async () => {
    form.setValue('step', 0);
    const isValid = await form.trigger();
    if (!isValid) return;

    const values = form.getValues();
    if (!values.season_uuid) {
      toast({
        status: 'error',
        title: t('pages.application_season.components.season_step1.toasts.missing_season_id'),
      });
      return;
    }

    const seasonUuid = values.season_uuid;
    setIsSubmitting(true);
    try {
      // 1) Stages — transação atômica.
      const stagesItems = buildStagesBatchItems(values);
      const createdStages = await postStagesBatch.mutateAsync({
        season_uuid: seasonUuid,
        items: stagesItems,
      });

      // 2) Season teams — associa todos os times escolhidos no Step 3.
      const seasonTeamUuids = values.season_teams.map(item => item.team_uuid);
      if (seasonTeamUuids.length > 0) {
        await postSeasonTeamsBatch.mutateAsync({
          season_uuid: seasonUuid,
          team_uuids: seasonTeamUuids,
        });
      }

      // 3) Stage teams (HYBRID): distribui os times nos grupos.
      // `buildStagesBatchItems` envia grupos com stage_order 1..N antes dos
      // mata-mata; o backend retorna na mesma ordem, então pareamos por índice.
      if (values.season_type === SeasonType.HYBRID) {
        const createdGroupStages = createdStages.filter(
          stage => stage.type === StageType.GROUP,
        );
        await Promise.all(
          values.group_stages.map(async (groupItem, index) => {
            const stageUuid = createdGroupStages[index]?.uuid;
            if (!stageUuid || groupItem.team_uuids.length === 0) return;
            await postStageBatchTeams.mutateAsync({
              id: stageUuid,
              payload: { team_uuids: groupItem.team_uuids },
            });
          }),
        );
      }

      toast({
        status: 'success',
        title: t('pages.application_season.components.season_step3.toasts.create_success'),
      });
      navigate(SEASONS_LIST_ROUTE);
    } catch (error: unknown) {
      toast({
        status: 'error',
        title:
          (error as Error)?.message ||
          t('pages.application_season.components.season_step3.toasts.create_error'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps: WizardStep[] = [
    {
      title: t('pages.application_season.components.season_wizard.tabs.tab_info'),
      content: (
        <Step1SeasonInfo
          mode="create"
          onAdvance={() => goToStep(1)}
          onCancel={handleCancel}
        />
      ),
    },
    {
      title: t('pages.application_season.components.season_wizard.tabs.tab_stages'),
      content: (
        <Step2SeasonStages
          onBack={() => setTabIndex(0)}
          onAdvance={() => goToStep(2)}
          onCancel={handleCancel}
        />
      ),
    },
    {
      title: t('pages.application_season.components.season_wizard.tabs.tab_teams'),
      content: (
        <Step3SeasonTeams
          onBack={() => setTabIndex(1)}
          onAdvance={() => goToStep(3)}
          onCancel={handleCancel}
        />
      ),
    },
    {
      title: t('pages.application_season.components.season_wizard.tabs.tab_review'),
      content: (
        <Step4SeasonReview
          onBack={() => setTabIndex(2)}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
          isSubmitting={isSubmitting}
        />
      ),
    },
  ];

  return (
    <PageContent
      title={t('pages.application_season.components.season_create.page_title')}
      breadcrumbItems={breadcrumb}
    >
      <FormProvider {...form}>
        <SeasonWizardTabs
          steps={steps}
          tabIndex={tabIndex}
          furthestStep={furthestStep}
          onTabChange={setTabIndex}
        />
      </FormProvider>
    </PageContent>
  );
};
